import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  useInsertData,
  useInsertDataWithImage,
} from "../../hooks/useInsertData";
import { useGetData } from "../../hooks/useGetData";
import {
  useUpdateData,
  useUpdateDataWithImage,
} from "../../hooks/useUpdateDate";
import useDeleteData from "../../hooks/useDeleteData";

export const createMaintenanceItem = createAsyncThunk(
  "maintenanceItem/createMaintenanceItem",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useInsertData("maintenanceItems", data);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const getAllMaintenanceItems = createAsyncThunk(
  "maintenanceItem/getAllMaintenanceItems",
  async (page = 1, { rejectWithValue }) => {
    try {
      const res = await useGetData("maintenanceItems?page" + page);
      return res;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const getSpecificMaintenanceItem = createAsyncThunk(
  "maintenanceItem/getSpecificMaintenanceItem",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useGetData(`maintenanceItems/${id}`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const getScheduleStateMaintenanceItem = createAsyncThunk(
  "maintenanceItem/getScheduleStateMaintenanceItem",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useGetData(`maintenanceItems/schedule/stats/${id}`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const getDeletedMaintenanceItem = createAsyncThunk(
  "maintenanceItem/getDeletedMaintenanceItem",
  async (_, { rejectWithValue }) => {
    try {
      const res = await useGetData(`maintenanceItems/deleted`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const createBulkMaintenanceItem = createAsyncThunk(
  "maintenanceItem/createBulkMaintenanceItem",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useInsertData(`maintenanceItems/bulkCreateItems`, data);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const createBulkMaintenanceItemByContract = createAsyncThunk(
  "maintenanceItem/createBulkMaintenanceItemByContract",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useInsertData(
        `maintenanceItems/bulkCreateItemsByContract`,
        data
      );
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const updateMaintenanceItem = createAsyncThunk(
  "maintenanceItem/updateMaintenanceItem",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useUpdateData(`maintenanceItems/bulkUpdateItems`, data);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const deleteMaintenanceItem = createAsyncThunk(
  "maintenanceItem/deleteMaintenanceItem",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useDeleteData(`maintenanceItems/force/${id}`);
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

export const addImageForMaintenanceItem = createAsyncThunk(
  "maintenanceItem/addImageForMaintenanceItem",
  async ({ data, id }, { rejectWithValue }) => {
    try {
      const res = await useInsertDataWithImage(
        `maintenanceItems/image/${id}`,
        data
      );
      return res.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);

const initialState = {
  maintenanceItem: null,
  allMaintenanceItems: null,
  bulkMaintenanceItem: null,
  bulkMaintenanceItemByContract: null,
  statusUpdateMaintenanceItem: null,
  status: "idle",
  error: null,
  token: null,
};

export const maintenanceItemSlice = createSlice({
  name: "MaintenanceItemSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.maintenanceItem = action.payload;
      })
      .addCase(createMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(getSpecificMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getSpecificMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.maintenanceItem = action.payload;
      })
      .addCase(getSpecificMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(updateMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.statusUpdateMaintenanceItem = action.payload;
      })
      .addCase(updateMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(createBulkMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createBulkMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.bulkMaintenanceItem = action.payload;
      })
      .addCase(createBulkMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(createBulkMaintenanceItemByContract.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        createBulkMaintenanceItemByContract.fulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.bulkMaintenanceItemByContract = action.payload;
        }
      )
      .addCase(
        createBulkMaintenanceItemByContract.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
        }
      )
      .addCase(getAllMaintenanceItems.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllMaintenanceItems.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allMaintenanceItems = action.payload;
      })
      .addCase(getAllMaintenanceItems.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(deleteMaintenanceItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteMaintenanceItem.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(deleteMaintenanceItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default maintenanceItemSlice.reducer;
